/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { memo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import Button from '@base/Button/Button';
import AddToList from '@icon/AddToList';
import AddedToList from '@icon/AddedToList';
import { fetchPartPaymentStatus } from '@actions/checkout';
import useSubscriptionExpired from '@hooks/useSubscriptionExpired';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import { getMeUsername, getIsLoggedIn } from '@selectors/auth';
import { Spacings } from '@styles/index';
import { subscriptionTypes, programmeTypes } from '@constants/index';
import useEducatorContext from '@hooks/useEducatorContext';
import useWindowSize from '@hooks/useWindowSize';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro,
  getIsSubscribedToLite
} from '@selectors/subscription';
import { getIsGTPGoal } from '@api/selectors/constants';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import getIsSubscribedForContent from '@utils/subscriptionHelpers/getIsSubscribedForContent';
import getContentEnrollButtonLabel from '@utils/subscriptionHelpers/getContentEnrollButtonLabel';
import { getCheckPartPaymentStatus } from '@api/selectors/auth';
import useConstants from '@api/hooks/useConstants';
import useAppInstallAnalytics from '@hooks/useAppInstallAnalytics';
import { CONTINUE_ON_APP } from '@constants/segment';

const SmallHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_8B};
  @media (max-width: 47.95em) {
    padding: 16px;
  }
`;

const CourseTitle = styled(H3)`
  width: 73%;
  color: var(--color-text-primary);
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const EnrollButton = styled(Button)`
  margin-left: ${Spacings.SPACING_16B};
  margin-top: ${({ scroll, isEducatorSide }) => {
    if (isEducatorSide) return '0px';
    return scroll ? Spacings.SPACING_2B : Spacings.SPACING_5B;
  }};
  @media (max-width: 47.95em) {
    margin-left: 0px;
    width: 100%;
    font-size: 14px;
    padding: 16px 0;
    height: auto;
  }
`;

const SubHeaderContent = ({
  course: plusCourse,
  isTestSeries,
  dataAnalytics,
  handleEnroll,
  hideCTA
}) => {
  const { goalUid, isEnrolled } = plusCourse;
  const { isMobile } = useWindowSize();

  const dispatch = useDispatch();

  const isLoggedIn = useSelector(getIsLoggedIn);
  const username = useSelector(getMeUsername);
  const { accessControl } = useAccessControl(goalUid);
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);
  const sendAapInstallAnalytics = useAppInstallAnalytics();
  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus)
      dispatch(fetchPartPaymentStatus(goalUid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalUid, isLoggedIn, checkPartPaymentStatus]);

  const isEducatorSide = useEducatorContext();
  const isGTP = useConstants(getIsGTPGoal(goalUid));

  const CTALabel = usePurchaseCTAHandler(goalUid);
  const plusExpired = useSubscriptionExpired(goalUid);
  const iconicExpired = useSubscriptionExpired(
    goalUid,
    subscriptionTypes.ICONIC
  );
  const iconicProExpired = useSubscriptionExpired(
    goalUid,
    subscriptionTypes.ICONIC_PRO
  );
  const isIconicUser = useSelector(getIsSubscribedToIconic(goalUid));
  const isIconicProUser = useSelector(getIsSubscribedToIconicPro(goalUid));
  const isPlusUser = useSelector(getIsSubscribedToGoal(goalUid));
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUid));
  const isFreeUser =
    !isIconicUser && !isIconicProUser && !isPlusUser && !isLiteUser;

  if (!plusCourse) return null;
  const { name, isSpecial, isIconic, isIconicPro } = plusCourse;

  const isSubscribed = getIsSubscribedForContent({
    isIconic,
    isIconicPro,
    isIconicUser,
    isIconicProUser,
    isPlusUser
  });

  const isHybridCourse =
    plusCourse.courseType === programmeTypes.HYBRID_COURSE ||
    plusCourse.courseType === programmeTypes.HYBRID_TESTSERIES;
  const getButtonLabel = () => {
    if (isMobile) return CONTINUE_ON_APP;
    if (isEducatorSide) return 'Discussion Forum';
    return getContentEnrollButtonLabel({
      isSpecial,
      isEnrolled,
      isSubscribed,
      isIconic,
      isIconicPro,
      iconicExpired,
      iconicProExpired,
      plusExpired,
      isIconicUser,
      isIconicProUser,
      isPlusUser,
      ctaLabel: CTALabel,
      enrolledStateLabel: 'enrolled'
    });
  };

  const getButtonTheme = () => {
    if (isMobile) return 'green';
    if (isEducatorSide) return 'blue';
    if (isSpecial) return isEnrolled ? 'black' : 'green';
    if (
      (iconicExpired && isIconic) ||
      (iconicProExpired && isIconicPro) ||
      plusExpired
    )
      return 'green';
    if (isSubscribed && isEnrolled) return 'black';
    return 'green';
  };

  const getButtonVariant = () => {
    if (isGTP) return 'disabled';
    if (isMobile) return 'filled';
    if (isEducatorSide) return 'filled';
    if (isSpecial) return isEnrolled ? 'hollow' : 'filled';
    if (
      (iconicExpired && isIconic) ||
      (iconicProExpired && isIconicPro) ||
      plusExpired
    )
      return 'filled';
    if (isSubscribed && isEnrolled) return 'hollow';
    return 'filled';
  };

  const getIcon = () => {
    if (isEducatorSide || isMobile) return null;
    if (isSpecial)
      return isEnrolled ? (
        <AddedToList
          size={Spacings.SPACING_6B}
          color="var(--color-text-primary)"
        />
      ) : (
        <AddToList color="var(--color-base-1)" size={Spacings.SPACING_6B} />
      );
    if (isSubscribed && isEnrolled)
      return (
        <AddedToList
          size={Spacings.SPACING_6B}
          color="var(--color-text-primary)"
        />
      );
    if (isSubscribed || isSpecial)
      return (
        <AddToList color="var(--color-base-1)" size={Spacings.SPACING_6B} />
      );
  };

  const handleOnEnrollButtonClick = (e) => {
    const ctaLabel = getButtonLabel();
    if (ctaLabel === CONTINUE_ON_APP) {
      sendAapInstallAnalytics({
        button_text: ctaLabel
      });
    }
    handleEnroll(e, true);
  };

  const renderEnrollButton = () => {
    if ((isEducatorSide && isTestSeries) || !!hideCTA) return null;
    const author = plusCourse.authors.map((educator) => educator.username);
    if (isEducatorSide && !author.includes(username)) return null;
    return (
      <EnrollButton
        variant={getButtonVariant()}
        label={getButtonLabel()}
        color={getButtonTheme()}
        onClick={handleOnEnrollButtonClick}
        dataAnalytics={`${dataAnalytics}-courseEnroll`}
        isEducatorSide
        icon={getIcon()}
      />
    );
  };

  return (
    <SmallHeader>
      <CourseTitle lineClamp={1}>{name}</CourseTitle>
      {(!isHybridCourse || isFreeUser) && renderEnrollButton()}
    </SmallHeader>
  );
};

export default memo(SubHeaderContent);

SubHeaderContent.propTypes = {
  handleEnroll: PropTypes.func.isRequired,
  isTestSeries: PropTypes.bool,
  dataAnalytics: PropTypes.string
};

SubHeaderContent.defaultProps = {
  isTestSeries: false,
  dataAnalytics: ''
};
